import React from "react";
import { FaRegArrowAltCircleDown, FaRegArrowAltCircleUp } from "react-icons/fa";
import styled from "styled-components";

// Estilo do container principal
const Container = styled.div`
  width: 100%;
  max-width: 1300px;
  margin: 40px auto;
  padding: 30px;
  background-color: #f4f4f9;
  border-radius: 20px;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.1);
  font-family: 'Arial', sans-serif;
`;

// Título centralizado com fontes modernas
const Header = styled.h1`
  font-size: 2.5rem;
  color: #101828;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 700;
`;

// Estilos do conteúdo, com layout flexível e responsivo
const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Estilo para cada item de FAQ
const FaqItem = styled.div`
  background-color: #ffffff;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0px 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

// Estilo do título de cada pergunta
const FaqQuestion = styled.h3`
  font-size: 1.6rem;
  color: #101828;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

// Estilo para a resposta do FAQ
const FaqAnswer = styled.p`
  font-size: 1.2rem;
  color: #333;
  margin-top: 10px;
  display: ${({ open }) => (open ? "block" : "none")};
`;

// Estilo do formulário de feedback
const FeedbackForm = styled.div`
  margin-top: 40px;
  padding: 25px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.1);
`;

// Título do formulário de feedback
const FormTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 20px;
  color: #101828;
  font-weight: 700;
`;

// Estilo para os campos de input
const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 1rem;

  &:focus {
    border-color: #007aff;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 1rem;

  &:focus {
    border-color: #007aff;
    outline: none;
  }
`;

const SubmitButton = styled.button`
  background-color: #007aff;
  color: #ffffff;
  padding: 14px 25px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 1.2rem;
  width: 100%;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const SuccessMessage = styled.p`
  color: green;
  font-size: 1.2rem;
  margin-top: 15px;
`;

// Componente principal
const Faq = () => {
  const [openIndex, setOpenIndex] = React.useState(null);
  const [feedback, setFeedback] = React.useState({ name: "", email: "", suggestion: "" });
  const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);

  const faqs = [
    {
      question: "Como posso aplicar para um visto de trabalho com a ajuda das empresas de realocação?",
      answer: "Através de parcerias com empresas de realocação especializadas, nosso site facilita o processo de obtenção de vistos de trabalho, ajudando você a entender os requisitos e a escolher a empresa certa para seu caso.",
    },
    {
      question: "Como funcionam as parcerias com empresas de realocação?",
      answer: "Oferecemos um espaço exclusivo para empresas de realocação se cadastrarem e compartilharem seus serviços com usuários que buscam ajuda com o processo de mudança internacional. A parceria é simples e feita diretamente pelo nosso portal.",
    },
    {
      question: "Quais são os benefícios de cadastrar minha empresa de realocação?",
      answer: "Ao se cadastrar em nossa plataforma, sua empresa terá acesso a um público global interessado em serviços de realocação, o que pode aumentar significativamente as oportunidades de negócios e expandir sua presença internacional.",
    },
    {
      question: "Quais países são atendidos pelas empresas cadastradas?",
      answer: "As empresas cadastradas em nossa plataforma oferecem serviços em uma ampla gama de países. Você pode encontrar empresas que atendem destinos populares como Estados Unidos, Reino Unido, Alemanha, Portugal e muito mais.",
    },
    {
      question: "Quais documentos são necessários para o processo de realocação?",
      answer: "Cada empresa de realocação exige uma documentação específica, mas geralmente inclui comprovante de identidade, histórico profissional, e outros documentos relacionados ao visto e à moradia. Ao se cadastrar no site, você receberá orientações detalhadas sobre os requisitos de cada destino.",
    },
  ];

  const toggleAnswer = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleFeedbackChange = (e) => {
    setFeedback({ ...feedback, [e.target.name]: e.target.value });
  };

  const handleSubmitFeedback = (e) => {
    e.preventDefault();
    console.log("Feedback enviado: ", feedback);
    setFeedback({ name: "", email: "", suggestion: "" });
    setFeedbackSubmitted(true);
  };

  return (
    <Container>
      <Header>Perguntas Frequentes</Header>
      <FaqList>
        {faqs.map((faq, index) => (
          <FaqItem key={index}>
            <FaqQuestion onClick={() => toggleAnswer(index)}>
              {faq.question}
              {openIndex === index ? <FaRegArrowAltCircleUp /> : <FaRegArrowAltCircleDown />}
            </FaqQuestion>
            <FaqAnswer open={openIndex === index}>{faq.answer}</FaqAnswer>
          </FaqItem>
        ))}
      </FaqList>

      <FeedbackForm>
        <FormTitle>Deixe seu Feedback ou Sugestão</FormTitle>
        <form onSubmit={handleSubmitFeedback}>
          <Input
            type="text"
            name="name"
            placeholder="Seu nome"
            value={feedback.name}
            onChange={handleFeedbackChange}
          />
          <Input
            type="email"
            name="email"
            placeholder="Seu email (opcional)"
            value={feedback.email}
            onChange={handleFeedbackChange}
          />
          <TextArea
            name="suggestion"
            rows="5"
            placeholder="Sugestões ou feedbacks"
            value={feedback.suggestion}
            onChange={handleFeedbackChange}
          />
          <SubmitButton type="submit">Enviar Feedback</SubmitButton>
        </form>
        {feedbackSubmitted && <SuccessMessage>Obrigado pelo seu feedback!</SuccessMessage>}
      </FeedbackForm>
    </Container>
  );
};

export default Faq;
